import useLocalization from "@/hooks/useLocalization";
import { Box, alpha, useTheme } from "@mui/material";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DashboardChartTooltipProps,
  DashboardChartMetrics,
  DashboardGraphScope,
} from "../types";
import { addBucketLabels, formatCompactRevenue, formatRevenue } from "../utils";
import ChartCard from "./ChartCard";
import DashboardTooltip from "./DashboardTooltip";

type RevenueOverTimeChartProps = {
  graphScope: DashboardGraphScope;
  metrics: DashboardChartMetrics[];
};

type RevenueTooltipPayload = {
  color?: string;
  dataKey?: string;
  value?: number | string;
};

export default function RevenueOverTimeChart({
  graphScope,
  metrics,
}: RevenueOverTimeChartProps) {
  const theme = useTheme();
  const { t, adapterLocale } = useLocalization();
  const averageRevenueColor = theme.palette.info.main;
  const cancellationNoShowRevenueColor = theme.palette.warning.main;
  const chartData = addBucketLabels(
    metrics.map((metric) => ({
      ...metric,
      averageRevenuePerGuest:
        metric.guests === 0
          ? null
          : Math.round(metric.revenue / metric.guests),
    })),
    graphScope,
    adapterLocale,
  );
  const getRevenueTooltipLabel = (dataKey: string) => {
    switch (dataKey) {
      case "averageRevenuePerGuest":
        return t("dashboard:averageRevenuePerGuest");
      case "cancellationNoShowRevenue":
        return t("dashboard:cancellationNoShowRevenue");
      default:
        return t("dashboard:revenue");
    }
  };
  const renderTooltip = ({
    active,
    label,
    payload,
  }: DashboardChartTooltipProps) => {
    if (!active || !payload?.length) return null;

    const tooltipPayload = payload as RevenueTooltipPayload[];

    return (
      <DashboardTooltip
        title={label}
        items={tooltipPayload.flatMap((item) =>
          item.value === null || item.value === undefined
            ? []
            : [
                {
                  color: item.color,
                  label: getRevenueTooltipLabel(String(item.dataKey)),
                  value: formatRevenue(Number(item.value)),
                },
              ],
        )}
      />
    );
  };

  return (
    <ChartCard title={t("dashboard:revenueOverTime")}>
      <Box
        sx={{
          height: 320,
          overflow: "visible",
          "& .recharts-surface, & .recharts-wrapper": {
            overflow: "visible",
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />
            <XAxis dataKey="bucketLabel" stroke={theme.palette.text.secondary} />
            <YAxis
              yAxisId="revenue"
              tickFormatter={(value) => formatCompactRevenue(Number(value))}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <YAxis
              yAxisId="averageRevenuePerGuest"
              orientation="right"
              tickFormatter={(value) => formatCompactRevenue(Number(value))}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <Tooltip
              content={renderTooltip}
              contentStyle={{
                borderColor: theme.palette.divider,
                borderRadius: 8,
                boxShadow: theme.shadows[2],
              }}
              cursor={{
                stroke: alpha(theme.palette.primary.main, 0.35),
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="cancellationNoShowRevenue"
              name={t("dashboard:cancellationNoShowRevenue")}
              yAxisId="revenue"
              stroke={cancellationNoShowRevenueColor}
              strokeWidth={2}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              name={t("dashboard:revenue")}
              yAxisId="revenue"
              stroke={theme.palette.primary.main}
              fill="url(#revenueGradient)"
              isAnimationActive={false}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="averageRevenuePerGuest"
              name={t("dashboard:averageRevenuePerGuest")}
              yAxisId="averageRevenuePerGuest"
              stroke={averageRevenueColor}
              strokeWidth={2}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );
}
